import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { selectChartShowThresholdColor } from '@app/main/main-screen/store/main-selection/main-shared-selection/main-shared-selection.selectors';
import { ElementWidthClassDirective } from '@app/shared/directives/element-width-class-directive';
import { POPUP_MINIMIZED_WIDTH_SIZE } from '@app/main/main-content/floating-popup-manager/configs/floating-popup.config';
import { IFloatingPopupInstance } from '@app/main/main-content/floating-popup-manager/interfaces/floating-popup-instance';
import { FpLoggerNoiseLeakScoreStore } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-leak-score/services/fp-logger-noise-leak-score/fp-logger-noise-leak-score.store';
import { FpLoggerNoiseLeakScoreChartService } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-leak-score/services/fp-logger-noise-leak-score-chart.service';
import { FloatingPopupBarChartWrapperComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-bar-chart-wrapper/floating-popup-bar-chart-wrapper.component';
import { FloatingPopupBaseErrorMessageComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-base-error-message/floating-popup-base-error-message.component';

@Component({
  selector: 'gc-fp-logger-noise-leak-score-minimized',
  standalone: true,
  imports: [ElementWidthClassDirective, FloatingPopupBarChartWrapperComponent, FloatingPopupBaseErrorMessageComponent],
  templateUrl: './fp-logger-noise-leak-score-minimized.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'h-full',
  },
})
export class FpLoggerNoiseLeakScoreMinimizedComponent {
  public readonly currentTabStore = inject(FpLoggerNoiseLeakScoreStore);
  protected readonly translateService = inject(TranslateService);
  protected readonly chartService = inject(FpLoggerNoiseLeakScoreChartService);
  private readonly store = inject(Store);

  public popup = input<IFloatingPopupInstance>();

  public readonly POPUP_MINIMIZED_WIDTH_SIZE = POPUP_MINIMIZED_WIDTH_SIZE;

  private readonly chartShowThresholdColor = toSignal(
    this.store.select(selectChartShowThresholdColor).pipe(map(value => !!value)),
    { initialValue: false }
  );

  private readonly popupMainData = toSignal(this.currentTabStore.popupMainData$, {
    initialValue: null,
  });

  private readonly data = toSignal(this.currentTabStore.data$, {
    initialValue: null,
  });

  public error = toSignal(this.currentTabStore.error$, {
    initialValue: null,
  });

  public isChartDataExist = computed(() => !!this.data()?.length);

  public readonly loggerDeployment = computed(() => this.popupMainData()?.deployment);

  public readonly chartPlugins = [this.chartService.getBottomCirclesPlugin()];

  public combinedChartData = computed(() => {
    const data = this.data() || [];
    const showThresholdColor = this.chartShowThresholdColor();

    const chartData = this.chartService.getMinimizedDataForChart(data, { showThresholdColor });
    const chartOptions = this.chartService.generateMinimizedOptions();

    return { chartData, chartOptions };
  });
}
